/**
 * Отложенная загрузка сторонних скриптов (GA, чат-виджеты) — вне критического пути LCP/INP.
 */

import { AW_CONVERSION_ID, GA_MEASUREMENT_ID } from "@/lib/analytics";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_ID = GA_MEASUREMENT_ID;
const AW_ID = AW_CONVERSION_ID;
const COOKIE_CONSENT_KEY = "cookie-consent";

function getCookieConsent(): string | null {
  try {
    return localStorage.getItem(COOKIE_CONSENT_KEY);
  } catch {
    return null;
  }
}

function initGoogleAnalytics(): void {
  if (document.getElementById("google-analytics-script")) return;

  const pre = document.createElement("link");
  pre.rel = "preconnect";
  pre.href = "https://www.googletagmanager.com";
  pre.crossOrigin = "anonymous";
  document.head.appendChild(pre);

  const script = document.createElement("script");
  script.id = "google-analytics-script";
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    (window.dataLayer as unknown[]).push(args);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_ID, { send_page_view: true });
  // Google Ads tag (конверсии / linker); один gtag.js на оба ID
  gtag("config", AW_ID);
}

let analyticsStarted = false;
let engageAttached = false;

function detachEngageListeners(onEngage: () => void): void {
  if (!engageAttached) return;
  engageAttached = false;
  window.removeEventListener("scroll", onEngage);
  window.removeEventListener("pointerdown", onEngage);
  window.removeEventListener("keydown", onEngage);
}

function startGoogleAnalytics(onEngage?: () => void): void {
  if (getCookieConsent() !== "accepted") return;
  if (onEngage) detachEngageListeners(onEngage);
  if (analyticsStarted) return;
  analyticsStarted = true;
  initGoogleAnalytics();
}

function scheduleGoogleAnalytics(): void {
  if (getCookieConsent() !== "accepted") return;

  const onEngage = () => startGoogleAnalytics(onEngage);
  engageAttached = true;
  window.addEventListener("scroll", onEngage, { passive: true });
  window.addEventListener("pointerdown", onEngage, { passive: true });
  window.addEventListener("keydown", onEngage, { passive: true });

  const idleLoad = () => startGoogleAnalytics(onEngage);
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(idleLoad, { timeout: 8000 });
  } else {
    setTimeout(idleLoad, 5000);
  }
}

/**
 * Запуск GA только после согласия на cookies (+ idle или первое взаимодействие).
 */
export function initDeferredAnalytics(): void {
  if (getCookieConsent() === "accepted") {
    if (document.readyState === "complete") {
      scheduleGoogleAnalytics();
    } else {
      window.addEventListener("load", scheduleGoogleAnalytics, { once: true });
    }
  }

  window.addEventListener(
    "cookie-consent-accepted",
    () => {
      scheduleGoogleAnalytics();
    },
    { once: false },
  );
}

export type LoadOnInteractionOptions = {
  /** Дополнительно вызвать после idle (мс), если пользователь не взаимодействовал. 0 = отключено. */
  idleFallbackMs?: number;
};

/**
 * Загрузить чат/виджет только после первого взаимодействия (снижает INP и блокировку main thread).
 *
 * @example
 * loadOnFirstInteraction(() => {
 *   import("./chatWidget").then((m) => m.mount("#chat-root"));
 * });
 */
export function loadOnFirstInteraction(
  loadFn: () => void,
  options: LoadOnInteractionOptions = {},
): () => void {
  let loaded = false;
  const { idleFallbackMs = 0 } = options;

  const run = () => {
    if (loaded) return;
    loaded = true;
    cleanup();
    loadFn();
  };

  const events = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
  const listenerOpts: AddEventListenerOptions = { passive: true, capture: true };

  const cleanup = () => {
    events.forEach((e) => window.removeEventListener(e, run, listenerOpts));
    if (idleId !== undefined) {
      if (typeof cancelIdleCallback === "function") cancelIdleCallback(idleId as number);
      else clearTimeout(idleId as ReturnType<typeof setTimeout>);
    }
  };

  events.forEach((e) => window.addEventListener(e, run, listenerOpts));

  let idleId: number | ReturnType<typeof setTimeout> | undefined;
  if (idleFallbackMs > 0) {
    const idleRun = () => run();
    if (typeof requestIdleCallback === "function") {
      idleId = requestIdleCallback(idleRun, { timeout: idleFallbackMs });
    } else {
      idleId = setTimeout(idleRun, idleFallbackMs);
    }
  }

  return cleanup;
}
